import { useState } from 'react';
import {
  Badge,
  Button,
  Choice,
  Csel,
  Destructive,
  Icon,
  Input,
  Popover,
  Switch,
  useToast,
} from '@efira/ui';
import { useNavigate } from 'react-router';

import {
  useAddWidget,
  usePatchWidget,
  useRemoveWidget,
  useSetWidgetShown,
  useSetWidgetsEnabled,
  useWidgets,
} from '../../api/queries';
import type { WidgetInstanceRow } from '../../api/queries';
import { useConsoleSession } from '../session';
import { useLayoutMode } from '../center/layoutMode';

/**
 * Виджеты оверлея: панель в шапке.
 *
 * ДВА БЛОКА, как у эталона: «Добавить» и «Показывать в этой игре». Разделение
 * не косметическое - это два разных вопроса. Первый: какие виджеты у меня
 * вообще есть (они живут на аккаунте). Второй: какие из них уместны в ЭТОЙ
 * игре - у заезда занят низ кадра, у арены центр.
 *
 * ОБЩИЙ ВЫКЛЮЧАТЕЛЬ ВКЛЮЧАЕТСЯ САМ при добавлении первого виджета: добавить
 * виджет и не увидеть его в кадре - это отказ без причины. Обратно он сам не
 * выключается никогда: решение «не показывать» принимает человек.
 *
 * ТИП НЕ ИСЧЕЗАЕТ ИЗ КАТАЛОГА ПОСЛЕ ДОБАВЛЕНИЯ. Экземпляров одного типа
 * сколько угодно: две цели по подписчикам - 1000 и 10 000 - нормальный
 * сценарий, и у эталона его выразить нечем.
 */
export function Widgets() {
  const session = useConsoleSession();
  const widgets = useWidgets();
  const enable = useSetWidgetsEnabled();
  const [configuring, setConfiguring] = useState<string | null>(null);

  // Панель доступна только при активной сессии: без игры показывать виджеты
  // негде, и кнопка вела бы в пустое окно.
  if (session.game === null) {
    return null;
  }

  const data = widgets.data;
  const count = data?.instances.length ?? 0;

  return (
    <Popover
      label="Виджеты оверлея"
      className="max-h-[70vh] w-80 overflow-y-auto p-3"
      placement="bottom-end"
      triggerClassName="gap-2"
      trigger={
        <span data-topbar="widgets-trigger" className="inline-flex items-center gap-2">
          <Icon name="layout" size="sm" />
          {/* Первая ступень лестницы полосы (`TopBar.tsx`): подпись уходит
              раньше всего остального, значок остаётся до 560 px. */}
          <span className="@max-[800px]:hidden">Виджеты</span>
          {count > 0 && <Badge variant="default">{count}</Badge>}
        </span>
      }
    >
      <div data-widgets="popover" className="flex flex-col gap-3">
        {/* Шапка панели: раскрытая панель теряла контекст - счётчик остался
            в шапке консоли, а внутри было непонятно, сколько виджетов. */}
        <p data-widgets="heading" className="text-foreground text-sm font-semibold">
          Виджеты{count > 0 ? ` · ${count}` : ''}
        </p>

        <div className="flex flex-col gap-1">
          <label className="text-foreground flex items-center gap-2 text-sm font-semibold">
            <Switch
              data-widgets="master"
              checked={data?.enabled === true}
              onCheckedChange={(next) => {
                enable.mutate(next === true);
              }}
            />
            Показывать виджеты на оверлее
          </label>
          {/* Тумблер без пояснения - вопрос без ответа: гасит он виджеты
              везде или только здесь. Рядом, в настройках эфира, у тумблеров
              пояснение есть. */}
          <p className="text-foreground/80 ps-10 text-sm">
            Общий выключатель: гасит виджеты во всех играх сразу. Показ в отдельной игре —
            переключателями ниже.
          </p>
        </div>

        {count > 0 && (
          <section className="flex flex-col gap-2">
            <p className="text-foreground/90 text-xs font-semibold tracking-wide uppercase">
              Показывать в этой игре
            </p>

            {(data?.instances ?? []).map((instance) => (
              <WidgetRow
                key={instance.id}
                instance={instance}
                gameId={session.gameId ?? ''}
                hidden={(data?.hidden ?? []).includes(instance.id)}
                open={configuring === instance.id}
                onToggleConfig={() => {
                  setConfiguring((current) => (current === instance.id ? null : instance.id));
                }}
              />
            ))}
          </section>
        )}

        <section className="flex flex-col gap-2">
          {/* Число в заголовке: нижний ряд карточек уходит под кромку
              панели, и без него не видно, что типов восемь, а не шесть. */}
          <p className="text-foreground/90 text-xs font-semibold tracking-wide uppercase">
            Добавить виджет · {(data?.catalog ?? []).length}
          </p>

          <div className="grid grid-cols-2 gap-2">
            {(data?.catalog ?? []).map((card) => (
              <CatalogCard key={card.type} card={card} />
            ))}
          </div>
        </section>
      </div>
    </Popover>
  );
}

/** Карточка каталога: «Добавить» или, у платного, «Что входит в Pro». */
function CatalogCard({
  card,
}: {
  card: { type: string; label: string; styles: readonly string[]; pro: boolean };
}) {
  const add = useAddWidget();
  const layout = useLayoutMode();
  const toast = useToast();
  const navigate = useNavigate();

  return (
    <div
      data-widgets-card={card.type}
      className="border-foreground/15 rounded-lg flex flex-col gap-1 border p-2"
    >
      <span className="text-sm">{card.label}</span>

      {card.pro ? (
        <Button
          variant="secondary"
          size="s"
          className="gap-1 self-start"
          data-widgets-unlock={card.type}
          onClick={() => {
            /* Чекаута у нас нет, и рисовать его нельзя. Ведём туда же, куда
               промо в левой колонке: «что входит в Pro». */
            void navigate('/app/plan?from=media');
          }}
        >
          <Icon name="crown" size="sm" />
          Входит в Pro
        </Button>
      ) : (
        <Button
          variant="secondary"
          size="s"
          className="self-start"
          data-widgets-add={card.type}
          /* Не залитая: восемь заливок основным цветом в одной панели
             обесценивают главную кнопку консоли, которая одна. Ворота
             «ровно одна главная» (`e2e/class-buttons.spec.ts`) считают
             панель виджетов поверхностью БЕЗ главного действия. */
          loading={add.isPending}
          onClick={() => {
            add.mutate(card.type, {
              onSuccess: () => {
                /* ОДНО СОБЫТИЕ - ОДНО СООБЩЕНИЕ. Было два тоста подряд:
                   «виджет добавлен» и «кадр в режиме раскладки». Это одно
                   действие, и второе сообщение - пояснение к первому, а не
                   отдельная новость. */
                toast.show({
                  tone: 'success',
                  title: `Виджет добавлен: «${card.label}»`,
                  text: 'Оверлей перешёл в режим раскладки — перетащите виджет туда, где он не мешает игре.',
                });

                /* Оверлей переходит в режим раскладки: новый виджет встал в
                   заводское место, и первое, что нужно, - поставить его
                   туда, где он не закроет игру. */
                layout.start();
              },
            });
          }}
        >
          Добавить
        </Button>
      )}
    </div>
  );
}

/** Строка добавленного виджета: показ в игре, настройки, удаление. */
function WidgetRow({
  instance,
  gameId,
  hidden,
  open,
  onToggleConfig,
}: {
  instance: WidgetInstanceRow;
  gameId: string;
  hidden: boolean;
  open: boolean;
  onToggleConfig: () => void;
}) {
  const shown = useSetWidgetShown(gameId);
  const patch = usePatchWidget();
  const remove = useRemoveWidget();
  const toast = useToast();

  return (
    <div
      data-widgets-row={instance.id}
      className="border-foreground/10 rounded-lg overflow-hidden border"
    >
      <div data-control-row="widget-row" className="flex items-start gap-1 p-2">
        <Switch
          data-widgets-row-toggle={instance.id}
          aria-label={`Показывать «${instance.label}» в этой игре`}
          checked={!hidden}
          onCheckedChange={(next) => {
            shown.mutate({ id: instance.id, shown: next === true });
          }}
          className="shrink-0"
        />

        {/* Название и шестерёнка делают одно и то же - как у эталона. */}
        <Button
          variant="ghost"
          className="min-w-0 flex-1 text-start break-words"
          data-widgets-row-configure={instance.id}
          aria-expanded={open}
          /* Без обрезки: «Цель по подп…» - половина названия ради пустого
             места справа. Две строки уместнее многоточия. */
          onClick={onToggleConfig}
        >
          {instance.label}
        </Button>

        <Button
          variant="ghost"
          size="s"
          className="px-1"
          data-widgets-row-gear={instance.id}
          aria-label={`Настроить «${instance.label}»`}
          onClick={onToggleConfig}
        >
          <Icon name="settings" size="sm" />
        </Button>

        <Destructive
          id="widget-delete"
          className="px-1"
          data={{ 'data-widgets-row-remove': instance.id }}
          ariaLabel={`Удалить «${instance.label}»`}
          icon={<Icon name="delete" size="sm" />}
          title={`Удалить «${instance.label}»?`}
          confirm="Удалить"
          onConfirm={() => {
            remove.mutate(instance.id, {
              onSuccess: () => {
                toast.show({ tone: 'success', title: 'Виджет убран с оверлея' });
              },
            });
          }}
        >
          Виджет уйдёт с оверлея вместе с его подписью, настройками и местом в раскладке.
        </Destructive>
      </div>

      {open && (
        <div data-widgets-config={instance.id} className="flex flex-col gap-2 p-2 pt-0">
          <label className="flex flex-col gap-1 text-xs">
            Подпись
            <Input
              data-widgets-config-label
              value={instance.label}
              onChange={(event) => {
                patch.mutate({ id: instance.id, label: event.target.value });
              }}
            />
          </label>

          {instance.target !== undefined && (
            <label className="flex flex-col gap-1 text-xs">
              Цель
              <Input
                data-widgets-config-target
                type="number"
                min={1}
                value={instance.target}
                onChange={(event) => {
                  patch.mutate({ id: instance.id, target: Number(event.target.value) || 1 });
                }}
              />
            </label>
          )}

          <div className="flex flex-col gap-1">
            <span className="text-foreground/80 text-xs">Вид</span>
            <Choice
              label="Вид"
              value={instance.style}
              onChange={(style) => {
                patch.mutate({ id: instance.id, style });
              }}
              size="sm"
              options={instance.styles.map((style) => ({
                value: style,
                label: STYLE_NAMES[style] ?? style,
                data: { 'data-widgets-config-style': style },
              }))}
            />
          </div>

          <label className="flex items-center gap-2 text-xs">
            Цвет
            {/* eslint-disable-next-line no-restricted-syntax -- выбор цвета: у пресета компонента нет, искали color-picker в реестре shadcn - нет ни в ui, ни в блоках */}
            <input
              type="color"
              data-widgets-config-color
              className="h-7 w-16"
              value={instance.accent}
              onChange={(event) => {
                patch.mutate({ id: instance.id, accent: event.target.value });
              }}
            />
          </label>

          {(
            [
              ['showValue', 'Показывать числа'],
              ['showIcon', 'Показывать значок'],
              ['card', 'Подложка под виджетом'],
            ] as const
          ).map(([field, title]) => (
            <label key={field} className="flex items-center gap-2 text-xs">
              <Switch
                data-widgets-config-toggle={field}
                checked={instance[field]}
                onCheckedChange={(next) => {
                  patch.mutate({ id: instance.id, [field]: next === true });
                }}
              />
              {title}
            </label>
          ))}

          {instance.rows !== undefined && (
            <label className="flex flex-col gap-1 text-xs">
              Сколько строк
              <Csel
                value={String(instance.rows)}
                aria-label="Сколько строк"
                triggerClassName="w-full"
                options={[3, 5, 8, 10].map((rows) => ({
                  value: String(rows),
                  label: `${rows}`,
                }))}
                onChange={(next) => {
                  patch.mutate({ id: instance.id, rows: Number(next) });
                }}
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

/** Названия видов на языке стримера, а не ключами схемы. */
const STYLE_NAMES: Record<string, string> = {
  milestone: 'Ступени',
  orbit: 'Круг',
  minimal: 'Просто',
  heart: 'Сердце',
  heartbeat: 'Пульс',
  jar: 'Копилка',
  stack: 'Стопка',
  ripple: 'Круги',
  wave: 'Волна',
  clock: 'Часы',
  list: 'Список',
  podium: 'Пьедестал',
  contain: 'Целиком',
  cover: 'Во всю ширину',
};
